import {omit} from 'lodash-es';
import type {Dispatch, SetStateAction} from 'react';
import {useCallback, useMemo, useState} from 'react';

import {Button, Pagination} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import useFilters from '@/hooks/useFilters';
import type {
  MarketPlaceTrade,
  MarketPlaceTradeQuery,
} from '@/types/MarketPlace';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';

import {PAGE_LIMIT} from '../utils/constants';
import TradeDetailDrawer from './TradeDetailDrawer';
import TradesFilters from './TradesFilters';
import TradeSummaryCards from './TradeSummaryCards';
import TradeTable from './TradeTable';
import useMarketPlaceTrades from './useMarketPlaceTrades';

// The applied filters live in the parent so they survive a tab switch (Tabs
// only mounts the active tab). The page stays here: Pagination resets to the
// first page whenever it mounts, as it does for the offers and bids tabs.
type TradesTabProps = {
  /** Set when the offers tab asked to show one offer's trades. */
  askId?: number | null;
  onClearAskId: () => void;
  appliedFilters: MarketPlaceTradeQuery;
  setAppliedFilters: Dispatch<SetStateAction<MarketPlaceTradeQuery>>;
};

const TradesTab = ({
  askId,
  onClearAskId,
  appliedFilters,
  setAppliedFilters,
}: TradesTabProps) => {
  // Staged in the panel until Apply; starts from what is applied, so the panel
  // shows the current filters after a tab switch.
  const [filtersToApply, setFiltersToApply] =
    useState<MarketPlaceTradeQuery>(appliedFilters);
  const [page, setPage] = useState(0);
  const [selectedTradeId, setSelectedTradeId] = useState<number | null>(null);

  const {applyFilters} = useFilters<MarketPlaceTradeQuery>(setAppliedFilters);

  const effectiveFilters = useMemo(
    () => (askId ? {...appliedFilters, askId} : appliedFilters),
    [appliedFilters, askId],
  );

  const {
    trades,
    summary,
    summaryStatus,
    total,
    isLoading,
    hasLoaded,
    error,
    retry,
  } = useMarketPlaceTrades(effectiveFilters, page);

  // A refetch keeps the previous rows mounted, so it needs its own signal.
  const isRefreshing = isLoading && hasLoaded;

  const selectAppliedFilters = useCallback(
    (name: keyof MarketPlaceTradeQuery, value: string | Date | undefined) => {
      setFiltersToApply((current) => ({...current, [name]: value}));
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    const applied = applyFilters(filtersToApply);
    if (!applied) {
      return false;
    }
    setPage(0);
    return true;
  }, [applyFilters, filtersToApply]);

  const clearFilters = useCallback(() => {
    setFiltersToApply({});
    // An empty set has no dates to validate, so it skips applyFilters.
    setAppliedFilters({});
    setPage(0);
    onClearAskId();
  }, [onClearAskId, setAppliedFilters]);

  const clearAskId = useCallback(() => {
    setPage(0);
    onClearAskId();
  }, [onClearAskId]);

  const handleSearch = useCallback(
    (term: string) => {
      const search = term.trim() || undefined;
      // Same limit the server validates `search` against; longer is a 400.
      if (search && search.length > 100) {
        showErrorMessage('Search is limited to 100 characters.');
        return;
      }
      setFiltersToApply((current) => ({...current, search}));
      // Drop the key rather than store `undefined`: toQueryString sends that
      // as the literal string "undefined".
      setAppliedFilters((current) =>
        search ? {...current, search} : omit(current, 'search'),
      );
      setPage(0);
    },
    [setAppliedFilters],
  );

  const handlePageChange = useCallback((params: {page?: number}) => {
    setPage(Number(params.page ?? 0));
  }, []);

  return (
    <section className="pt-5">
      <TradesFilters
        filtersToApply={filtersToApply}
        appliedFilters={effectiveFilters}
        askId={askId}
        selectAppliedFilters={selectAppliedFilters}
        handleApplyFilters={handleApplyFilters}
        clearFilters={clearFilters}
        clearAskId={clearAskId}
        handleSearch={handleSearch}
      />

      <div className="relative" aria-busy={isRefreshing}>
        <div
          className={
            isRefreshing
              ? 'pointer-events-none opacity-70 transition-opacity'
              : 'transition-opacity'
          }
        >
          {/* Zeros before the first answer would read as "no trades". */}
          {hasLoaded && (
            <TradeSummaryCards summary={summary} status={summaryStatus} />
          )}

          {error !== null && (
            <div className="mt-3 flex items-center gap-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
              <span>{getErrorMessage(error, 'Could not load trades.')}</span>
              <Button variant="outline" colorScheme="red" onClick={retry}>
                Retry
              </Button>
            </div>
          )}

          <div className="mt-3">
            <TradeTable
              trades={trades}
              isLoading={isLoading}
              hasLoaded={hasLoaded}
              hasActiveFilters={Object.keys(effectiveFilters).length > 0}
              hasError={error !== null}
              onSelect={(trade: MarketPlaceTrade) =>
                setSelectedTradeId(trade.id)
              }
              onClearFilters={clearFilters}
            />
          </div>
        </div>

        {isRefreshing && (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            <div
              role="status"
              className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm dark:border-dark-borderMid dark:bg-dark-inputBg dark:text-gray-200"
            >
              <span
                aria-hidden="true"
                className="h-4 w-4 animate-spin rounded-full border-2 border-cyan-600 border-t-transparent dark:border-cyan-500"
              />
              Updating trades...
            </div>
          </div>
        )}
      </div>

      {total > PAGE_LIMIT && (
        <Pagination
          limit={PAGE_LIMIT}
          total={total}
          isSearch={Boolean(appliedFilters.search)}
          isPageReset={false}
          currentPageOverride={page + 1}
          onPageChange={handlePageChange as PaginationProps['onPageChange']}
        />
      )}

      <TradeDetailDrawer
        key={selectedTradeId ?? 'none'}
        tradeId={selectedTradeId}
        onClose={() => setSelectedTradeId(null)}
      />
    </section>
  );
};

export default TradesTab;
